<script setup>
import { useUsersStore } from '@/stores/users';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute , useRouter} from 'vue-router';
import { useI18n } from 'vue-i18n';

const users = useUsersStore()
const { getUsers } = storeToRefs(users)
const route = useRoute()
const uid = computed(() => route.query.uid)
const { t } = useI18n()
const router = useRouter()

const activeTab = ref(1)
const success = ref('')
const search = ref('') 

const usersByStatus = computed(() =>
  getUsers.value.filter(u => activeTab.value ? u.ac : !u.ac)
)

const filteredUsers = computed(() => {
  if (!search.value) return usersByStatus.value
  return usersByStatus.value.filter(u =>
    u.un.toLowerCase().includes(search.value.toLowerCase())
  )
})

const fetchUsers = async () => {
  await users.fetchUsers(uid.value)
}

watch(() => route.query, async () => {
  await fetchUsers()
})

onMounted(async () => {
  await fetchUsers()
})

const blockAll = async () => {
  await users.blockAllUsers().then(res => {
    if (res.status == 204) {
      success.value = 'blockUsers.users_disabled'
      fetchUsers()
    }
  })
}

const unblockAll = async () => {
  await users.unblockAllUsers().then(res => {
    if (res.status == 204) {
      success.value = 'blockUsers.users_enabled'
      fetchUsers()
    }
  })
}

const goToRegister = (user) => {
  const domain = window.location.origin
  const fullUrl = `${domain}/register?code=${user.cod}`

  navigator.clipboard.writeText(fullUrl)
}

</script>

<template>
  <div>
    <div class="px-4 my-2">
      <RouterLink to="/users" class="text-sm font-semibold text-neutral-400" v-if="uid">&larr; {{$t('app.19')}}</RouterLink>
      <div class="SearchInput" >
        <div class="SearchInput_Icon" />
        <input
          class="SearchInput_Field"
          :placeholder="$t('app.8')"
          type="search"
          v-model="search"
        />
      </div>
    </div>

    <div class="px-4 h-12 flex justify-between items-center">
      <ul class="list-none flex items-center space-x-2">
        <li class="tab-item" :class="{ active: activeTab == 1 }" @click="activeTab = 1">
          <span>{{ $t('app.20') }}</span>
        </li>
        <li class="tab-item" :class="{ active: activeTab == 0 }" @click="activeTab = 0">
          <span>{{ $t('app.21') }}</span>
        </li>
      </ul>

      <div>
        <button
          class="py-1.5 px-3 text-center text-red-600 font-bold text-[13px]"
          @click="blockAll"
          v-if="activeTab == 1"
        >
          {{ $t('blockUsers.disable_all') }}
        </button>
        <button
          class="py-1.5 px-3 text-center text-emerald-600 font-bold text-[13px]"
          @click="unblockAll"
          v-if="activeTab == 0"
        >
          {{ $t('blockUsers.enable_all') }}
        </button>
      </div>
    </div>

    <div class="py-2 px-4 bg-emerald-200/25 text-emerald-400 text-xs font-semibold" v-if="success">
      {{ t(success) }}
    </div>

    <div class="px-4 py-2">
      <ul class="list-none space-y-1">
        <li v-for="user in filteredUsers" :key="user._id">
          <div class="usm-Users">
            <RouterLink :to="`/users/${user._id}`" class="flex items-center">
              <div class="usm-UserStatus" :class="{ enabled: user.ac, disabled: !user.ac }" />
              <p class="usm-UsersName">{{ user.un }}</p>
            </RouterLink>

            <div class="flex items-center space-x-4">
              <p v-if="user.rl == 'agt'" class="user-cod" @click="goToRegister(user)">{{ user.cod }}</p>
              <RouterLink :to="`/users?uid=${user._id}`" class="usm-UserHierarchy" v-if="user.rl != 'clt'" />
              <RouterLink :to="`/users/${user._id}#raport`" class="usm-UserRaport" />
              <RouterLink :to="`/users/${user._id}#changePassword`" class="usm-UserLock" />
              <RouterLink :to="`/users/${user._id}#credits`" class="usm-UserBalances" />
            </div>
          </div>
        </li>
      </ul>

      <p v-if="!filteredUsers.length" class="text-center text-sm text-neutral-400 py-4">
        {{ $t('app.42') }}
      </p>
    </div>
  </div>
</template>


<style scoped>
.usm-Users {
    background-image: var(--mgc-bg);
    @apply flex items-center justify-between px-3 py-2 rounded;
}

.usm-UserStatus {
    @apply w-2.5 h-2.5 rounded-full block;
}
.usm-UserStatus.enabled {
    @apply bg-emerald-400;
}
.usm-UserStatus.disabled {
    @apply bg-red-600;
}
.usm-UsersName {
    @apply text-neutral-300 text-base font-bold ml-1.5;
}
.user-cod{
    @apply text-neutral-300 text-xs font-bold cursor-copy;
}
.usm-UserLock {
    @apply cursor-pointer;
    mask-image: url('/lock-closed.svg');
    mask-size: 18px 18px;
    height: 18px;
    width: 18px;
    background-color: var(--color-grey6);
}

.usm-UserBalances {
    @apply cursor-pointer;
    mask-image: url('/currency-dollar.svg');
    mask-size: 18px 18px;
    height: 18px;
    width: 18px;
    background-color: var(--color-grey6);
}

.usm-UserHierarchy {
    @apply cursor-pointer;
    mask-image: url('/users.svg');
    mask-size: 18px 18px;
    height: 18px;
    width: 18px;
    background-color: var(--color-grey6);
}
.usm-UserRaport{
    @apply cursor-pointer;
    mask-image: url('/raport.svg');
    mask-size: 18px 18px;
    height: 18px;
    width: 18px;
    background-color: var(--color-grey6);
}
.SearchInput {
    background-color: hsla(0,0%,100%,.1);
    border-radius: 50px;
    display: flex;
    @apply px-3;
}

.SearchInput_Icon {
    mask-image: url("/search.svg");
    mask-size: 18px 18px;
    mask-repeat: no-repeat;
    mask-position: center;
    min-width: 18px;
    background-color: #fff;
    z-index: 20;
    display: flex;
    flex-direction: column
}

.SearchInput_Field {
    @apply flex-1 text-sm !bg-transparent placeholder:text-white font-bold text-white py-2.5 border-0;
    @apply focus:ring-0;
}
</style>